import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { CUITData } from '../core/models/CUIT';
import { Observable, of, throwError } from 'rxjs';
import { PaginatedResult } from 'src/app/shared/grid/paginate.model';
import { IPaginatedServiceParamters } from '../core/models/ITable';
import { Sellpoint } from '../core/models/Sellpoint';
import { Person } from '../core/models/Person';
import { map, catchError } from 'rxjs/operators';

export interface ValidacionCuitRG4004 {
  esValido: boolean;
  mensaje?: string;
  razonSocial?: string;
  condicionIva?: string;
  puedeRG4004?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CuitService {
  constructor(private apiService: ApiService) {}

  getAllPaginated(
    paginatedArguments: IPaginatedServiceParamters,
  ): Observable<PaginatedResult<CUITData[]>> {
    return this.apiService.getPaginated(
      '/api/CuitData/GetAllCuitData',
      paginatedArguments,
    );
  }

  getAll(): Observable<CUITData[]> {
    return this.apiService.get('/api/CuitData/GetCuitData');
  }

  getAllRecipients(query: string): Observable<Person[]> {
    if (query === '') {
      return of([]);
    }
    return this.apiService.get('/api/CuitData/GetCuitRecipients', query);
  }  

  getCuit(id): Observable<CUITData> {
    return this.apiService.get(`/api/CuitDataOwner/${id}`);
  }

  addCuit(newCuit): Observable<any> {
    return this.apiService.post('/api/CuitData/', newCuit);
  }

  editCuit(id, cuit): Observable<any> {
    return this.apiService.put(`/api/CuitData/${id}`, cuit);
  }

  deleteCuit(id) {
    return this.apiService.delete(`/api/CuitData/${id}`);
  }

  getAllSellPoint(paginatedArguments?: IPaginatedServiceParamters): Observable<any> {
    if (paginatedArguments) {
      return this.apiService.getPaginated("/api/CuitData/SellPoint/GetAllSellPoint", paginatedArguments);
    } else {
      return this.apiService.get("/api/CuitData/SellPoint/GetAllSellPoint");
    }
  }


  /**
   *  VALIDAR SI UN CUIT PUEDE SER USADO PARA RG 4004
   */
  validarCuitParaRG4004(cuit: string): Observable<ValidacionCuitRG4004> {
    if (!this.validarFormatoCuit(cuit)) {
      return of({
        esValido: false,
        mensaje: 'Formato de CUIT inválido. Debe tener 11 dígitos.'
      });
    }

    if (!this.validarDigitoVerificadorCuit(cuit)) {
      return of({
        esValido: false,
        mensaje: 'CUIT inválido. Dígito verificador incorrecto.'
      });
    }

    return of({
      esValido: true,
      mensaje: 'CUIT con formato válido',
      puedeRG4004: true
    });
  }

  private validarFormatoCuit(cuit: string): boolean {
    const cuitRegex = /^\d{11}$/;
    return cuitRegex.test(cuit);
  }

  private validarDigitoVerificadorCuit(cuit: string): boolean {
    if (cuit.length !== 11) return false;

    const multiplicadores = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
    let suma = 0;

    for (let i = 0; i < 10; i++) {
      suma += parseInt(cuit[i]) * multiplicadores[i];
    }

    const resto = suma % 11;
    const digitoVerificador = resto < 2 ? resto : 11 - resto;

    return digitoVerificador === parseInt(cuit[10]);
  }

  obtenerInfoCuit(cuit: string): Observable<any> {
    const limpio = this.limpiarCuit(cuit);

    // Validaciones básicas de formato
    if (!this.validarFormatoCuit(limpio)) {
      console.warn('Formato de CUIT inválido para obtener info:', cuit);
      return of(null);
    }

    if (!this.validarDigitoVerificadorCuit(limpio)) {
      console.warn('CUIT inválido (dígito verificador) para obtener info:', cuit);
      return of(null);
    }

    // Llamar al backend NestJS que consulta AFIP directamente sin persistir
    return this.apiService
      .get(`/api/FacturaElectronica/emisorPorCuit/${limpio}`)
      .pipe(
        catchError((error) => {
          console.error('Error obteniendo info del CUIT desde backend:', error);
          const err = error || {};
          (err as any).afipAuthFailure = [401, 503].includes(err.status);
          return throwError(() => err);
        }),
      );
  }

  formatearCuit(cuit: string): string {
    if (!cuit || cuit.length !== 11) return cuit;
    return `${cuit.substring(0, 2)}-${cuit.substring(2, 10)}-${cuit.substring(10)}`;
  }

  limpiarCuit(cuit: string): string {
    return cuit ? cuit.replace(/[^0-9]/g, '') : '';
  }
}